import { Link } from "@tanstack/react-router";
import { Mail, Phone, MapPin } from "lucide-react";
import { footerCategories, footerCompany, footerLegal, site } from "@/data/site";
import { track } from "@/lib/analytics";

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-ink text-canvas">
      <div className="shell py-[clamp(3rem,6vw,5rem)]">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <p className="font-[family-name:var(--font-display)] text-2xl">
              Paul&apos;s Pens <span className="italic text-brass-glow">n</span> Things
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-canvas/70">
              Handcrafted pens, lathe-turned gifts and carved pieces made one at a time by Paul Sherman in
              Paragould, Arkansas.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li>
                <a
                  href={`mailto:${site.email}`}
                  onClick={() => track("click_email", { location: "footer" })}
                  className="inline-flex items-center gap-2.5 text-canvas/85 hover:text-brass-glow motion-craft transition-colors"
                >
                  <Mail aria-hidden="true" className="size-4 shrink-0" />
                  {site.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${site.phoneHref}`}
                  onClick={() => track("click_phone", { location: "footer" })}
                  className="inline-flex items-center gap-2.5 text-canvas/85 hover:text-brass-glow motion-craft transition-colors"
                >
                  <Phone aria-hidden="true" className="size-4 shrink-0" />
                  {site.phoneDisplay}
                </a>
              </li>
              <li className="inline-flex items-center gap-2.5 text-canvas/70">
                <MapPin aria-hidden="true" className="size-4 shrink-0" />
                {site.locality}
              </li>
            </ul>
          </div>

          <FooterColumn title="Creations" links={footerCategories} />
          <FooterColumn title="Paul's Pens n Things" links={footerCompany} />
          <FooterColumn title="Information" links={footerLegal} />
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-canvas/15 pt-6 text-xs text-canvas/55 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {site.name}. All rights reserved.
          </p>
          <p>Custom work is confirmed by Paul and invoiced securely through PayPal.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <nav aria-label={title}>
      <h2 className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-brass-glow">{title}</h2>
      <ul className="mt-4 space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-canvas/75 hover:text-canvas motion-craft transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
